/**
 * SortableTableControls — Jul 2026.
 *
 * Shared client-side table controls for SEO/landing tables (Founder-Led,
 * IPO Watch): search box with field "dot" selectors, fully sortable
 * column headers with direction chevrons, Clear Search / Clear Sort
 * buttons (Dashboard-style), and 25-rows-per-page pagination.
 *
 * Sorting rule: null / undefined / '' / '—' count as the LOWEST value
 * in any sort field (numbers → -Infinity, strings → '').
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const normNum = (v) => {
  const n = Number(v);
  return v == null || v === '' || v === '—' || !Number.isFinite(n) ? -Infinity : n;
};
const normStr = (v) => (v == null || v === '—' ? '' : String(v).toLowerCase());

export function useTableControls({ rows, searchFields, sortAccessors, pageSize = 25 }) {
  const [search, setSearch] = useState('');
  const [field, setField] = useState(searchFields[0]?.key);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const processed = useMemo(() => {
    let out = rows || [];
    const q = search.trim().toLowerCase();
    if (q) {
      const getter = searchFields.find((f) => f.key === field)?.get;
      if (getter) out = out.filter((r) => String(getter(r) || '').toLowerCase().includes(q));
    }
    if (sortKey && sortAccessors[sortKey]) {
      const { get, type } = sortAccessors[sortKey];
      const norm = type === 'str' ? normStr : normNum;
      out = [...out].sort((a, b) => {
        const va = norm(get(a));
        const vb = norm(get(b));
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [rows, search, field, sortKey, sortDir, searchFields, sortAccessors]);

  useEffect(() => { setPage(1); }, [search, field, sortKey, sortDir, rows]);

  const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = processed.slice((safePage - 1) * pageSize, safePage * pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(sortAccessors[key]?.type === 'str' ? 'asc' : 'desc');
    }
  };

  return {
    search, setSearch, field, setField,
    sortKey, sortDir, toggleSort,
    clearSearch: () => setSearch(''),
    clearSort: () => { setSortKey(null); setSortDir('desc'); },
    isSearchActive: search.trim().length > 0,
    isSortDefault: sortKey == null,
    page: safePage, setPage, pageSize, totalPages,
    totalRows: processed.length, pageRows,
  };
}

export function SortableTh({ ctrl, sortKey, label, align = 'left', className = '', testIdPrefix = 'table' }) {
  const active = ctrl.sortKey === sortKey;
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <th
      className={`${alignCls} px-3 py-2 cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap ${className}`}
      onClick={() => ctrl.toggleSort(sortKey)}
      data-testid={`${testIdPrefix}-sort-${sortKey}`}
      title={`Sort by ${label}`}
    >
      <span className={`inline-flex items-center gap-0.5 ${justify}`}>
        {label}
        {active && (ctrl.sortDir === 'asc'
          ? <ChevronUp className="w-3 h-3" data-testid={`${testIdPrefix}-chevron-up-${sortKey}`} />
          : <ChevronDown className="w-3 h-3" data-testid={`${testIdPrefix}-chevron-down-${sortKey}`} />)}
      </span>
    </th>
  );
}

export function TableToolbar({ ctrl, searchFields, placeholder = 'Search…', testIdPrefix = 'table' }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
      <div className="flex items-center gap-3 flex-wrap min-w-0 flex-1">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={ctrl.search}
            onChange={(e) => ctrl.setSearch(e.target.value)}
            placeholder={placeholder}
            className="pl-8 pr-3 py-1.5 rounded-md border border-border bg-background text-sm w-full sm:w-56 focus:outline-none focus:ring-1 focus:ring-sky-500/50"
            data-testid={`${testIdPrefix}-search-input`}
          />
        </div>
        {/* Field "dot" selectors */}
        <div className="flex items-center gap-3">
          {searchFields.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => ctrl.setField(f.key)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`${testIdPrefix}-search-field-${f.key}`}
            >
              <span className={`w-3 h-3 rounded-full border transition-colors ${ctrl.field === f.key
                ? 'bg-sky-600 border-sky-600 ring-2 ring-sky-500/25'
                : 'bg-background border-muted-foreground/50'}`} />
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {/* Clear buttons — right side, Dashboard style */}
      <div className="flex items-center gap-2 sm:ml-auto shrink-0">
        <button
          type="button"
          onClick={ctrl.clearSearch}
          disabled={!ctrl.isSearchActive}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testIdPrefix}-clear-search-btn`}
          title="Clear the search box"
        >
          <X className="w-3 h-3" /> Clear Search
        </button>
        <button
          type="button"
          onClick={ctrl.clearSort}
          disabled={ctrl.isSortDefault}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testIdPrefix}-clear-sort-btn`}
          title="Reset the column sort back to its default order"
        >
          <X className="w-3 h-3" /> Clear Sort
        </button>
      </div>
    </div>
  );
}

export function PaginationBar({ ctrl, testIdPrefix = 'table' }) {
  if (ctrl.totalRows === 0) return null;
  const start = (ctrl.page - 1) * ctrl.pageSize + 1;
  const end = Math.min(ctrl.page * ctrl.pageSize, ctrl.totalRows);
  return (
    <div className="flex items-center justify-between gap-2 mt-3 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-pagination`}>
      <span data-testid={`${testIdPrefix}-pagination-info`}>
        Showing {start}–{end} of {ctrl.totalRows}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => ctrl.setPage(ctrl.page - 1)}
          disabled={ctrl.page <= 1}
          className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md border border-border bg-background font-semibold hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`${testIdPrefix}-page-prev`}
        >
          <ChevronLeft className="w-3 h-3" /> Prev
        </button>
        <span className="font-mono px-1" data-testid={`${testIdPrefix}-page-indicator`}>
          {ctrl.page} / {ctrl.totalPages}
        </span>
        <button
          type="button"
          onClick={() => ctrl.setPage(ctrl.page + 1)}
          disabled={ctrl.page >= ctrl.totalPages}
          className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md border border-border bg-background font-semibold hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid={`${testIdPrefix}-page-next`}
        >
          Next <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
